import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { filter, Observable } from 'rxjs';
import { assertStore } from '../../../cross-team-shared/misc-utils/assert-store';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { RenderTriggerService } from '../../render/render-trigger.service';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { orthographicViewRegionName } from '../../state/view-region-state';

@Component({
  selector: 'h3-orthographic-renderer',
  templateUrl: './orthographic-renderer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RenderTriggerService],
  standalone: false,
})
export class OrthographicRendererComponent
  extends DirectiveSuperclass
  implements OnInit, AfterViewInit
{
  @Input({ required: true }) size!: Point2D;
  @Input({ required: true }) videoOutputName!: string;

  private readonly store = inject(PointCloudContentStore);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  protected size$!: Observable<Point2D>;
  protected viewRegionNames = [orthographicViewRegionName];

  private renderTriggerService = inject(RenderTriggerService);

  ngOnInit() {
    this.size$ = this.getInput$('size').pipe(filter(isDefined));
    this.renderTriggerService.addTrigger(this.size$);
    this.renderTriggerService.addTrigger(
      this.store('viewBoxes')(this.videoOutputName).$,
    );
    this.renderTriggerService.addTrigger(this.store('assetBoundingBox').$);
  }

  ngAfterViewInit() {
    this.subscribeToSize();
    this.updateViewRegion();
  }

  private subscribeToSize() {
    this.subscribeTo(this.size$, (size) => {
      this.elementRef.nativeElement.style.width = `${size[0]}px`;
      this.elementRef.nativeElement.style.height = `${size[1]}px`;
      this.store('render')('containerSize')(this.videoOutputName).set(size);
    });
  }

  private updateViewRegion() {
    const regionStore = this.store('viewRegions')('regions')(
      orthographicViewRegionName,
    );
    assertStore(regionStore, 'orthographic view region must exist');
    regionStore.setUsing((state) => ({
      ...state,
      outputName: this.videoOutputName,
    }));
  }
}
